import { ref } from 'vue';
import { MentionEntityType } from '@/entities/MentionEntityType.ts';
import { parseMentions } from '@/utils/parseMentions.ts';

export type NoteRecipientId = number | string;

const userMentionIds = (content: string): string[] =>
    parseMentions(content)
        .filter(
            (segment) =>
                segment.type === 'mention' &&
                segment.entityType === MentionEntityType.USERS &&
                segment.args[0] !== undefined
        )
        .map((segment) => String(segment.args[0]));

export const useNoteMentions = () => {
    const trackedMentionIds = ref<string[]>([]);

    const addMentionRecipient = (
        recipients: NoteRecipientId[],
        userId: NoteRecipientId
    ): NoteRecipientId[] => {
        const id = String(userId);

        if (recipients.some((recipientId) => String(recipientId) === id)) {
            return recipients;
        }

        if (!trackedMentionIds.value.includes(id)) {
            trackedMentionIds.value = [...trackedMentionIds.value, id];
        }

        return [...recipients, userId];
    };

    const syncFromContent = (content: string): void => {
        trackedMentionIds.value = userMentionIds(content);
    };

    const reconcileRecipients = (
        content: string,
        recipients: NoteRecipientId[]
    ): NoteRecipientId[] => {
        const survivingMentionIds = userMentionIds(content);

        return recipients.filter((recipientId) => {
            const id = String(recipientId);

            return !trackedMentionIds.value.includes(id) || survivingMentionIds.includes(id);
        });
    };

    const reset = (): void => {
        trackedMentionIds.value = [];
    };

    return {
        addMentionRecipient,
        reconcileRecipients,
        reset,
        syncFromContent,
    };
};
